import { useEffect, useState } from "react";
import AdjustmentsHorizontalIcon from "@heroicons/react/24/solid/AdjustmentsHorizontalIcon";
import MagnifyingGlassIcon from "@heroicons/react/24/solid/MagnifyingGlassIcon";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import { shallow } from "zustand/shallow";

import { useBreakpointContext } from "@hsl/core/hooks/useBreakpoint";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";

import { useSidebarContext } from "../../hooks/useSidebarToggle";
import useTranslation, { Translate } from "../../hooks/useTranslation";
import useExplorerStore from "../../store/store";
import Button from "../Button";
import Input from "../Input";
import DownloadDataButton from "./DownloadDataButton";

// biome-ignore lint/suspicious/noExplicitAny: legitimate use case
function useDebouncedEffect(callback: () => void, delay: number, deps: any[]) {
    // biome-ignore lint/correctness/useExhaustiveDependencies: deps are passed in by the caller
    useEffect(() => {
        const timer = setTimeout(() => {
            callback();
        }, delay);

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, deps);
}

const SearchBar = () => {
    const { toggleSidebar } = useSidebarContext();
    const { searchEvent } = useYardActionEvents();
    const bp = useBreakpointContext()?.currentBreakpoint;
    const T = useTranslation();
    const {
        applySearch,
        isActive,
        term,
        placeholder,
        placeholderShort,
        isFiltering,
        refine,
    } = useExplorerStore(
        ({
            applySearch,
            search: { isActive, term },
            isFiltering,
            config: {
                search: { placeholder, placeholderShort },
            },
            refine,
        }) => ({
            applySearch,
            isActive,
            term,
            placeholder,
            placeholderShort,
            isFiltering,
            refine,
        }),
        shallow,
    );
    const [dirty, setDirty] = useState(false);
    const handleInputFocus = () => setDirty(true);

    const inputPlaceholder =
        bp === "xs" ? placeholderShort ?? placeholder : placeholder;
    const numFilters = useExplorerStore(
        (store) =>
            store.filters.primary.length +
            store.filters.secondary.length +
            store.filters.advanced.length,
    );
    // debounced search
    // basically updates the <input> first, then applies the search
    // after, so that typing is as snappy as possible
    const [searchText, setSearchText] = useState(term);

    const createClearSearchHandler = (type: "search" | "all") => () => {
        setDirty(true);
        if (isActive) {
            setSearchText("");
            if (type === "all") {
                refine({}, true, true);
            }
        }
    };

    useDebouncedEffect(
        () => {
            dirty && applySearch(searchText);
        },
        300,
        [searchText, dirty],
    );

    useDebouncedEffect(
        () => {
            isActive && searchEvent(searchText);
        },
        2000,
        [isActive, searchText],
    );

    // When clearing search and filters ensure that state is reset
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const pSearch = params.get("search");
        if (term === "" && dirty) {
            setSearchText("");
        } else if (!dirty && pSearch) {
            setSearchText(pSearch);
        }
    }, [term, dirty]);

    return (
        <div className="flex items-center py-3">
            <div className="relative">
                <Button
                    icon={
                        <AdjustmentsHorizontalIcon className="ml-0 mr-0 h-10 w-10" />
                    }
                    className="mr-2 h-12 w-12 sm:mr-4 lg:hidden"
                    onClick={toggleSidebar}
                />
                {numFilters > 0 && (
                    <div className="bg-core-yellow text-charcoal absolute -bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full text-xs lg:hidden">
                        {numFilters}
                    </div>
                )}
            </div>
            <Input
                iconProps={{
                    icon: (
                        <button
                            type="button"
                            className={cx("appearance-none max-sm:hidden", {
                                "cursor-pointer": isActive,
                            })}
                            onClick={createClearSearchHandler("search")}
                        >
                            {isActive ? (
                                <XMarkIcon width={25} color="#000" />
                            ) : (
                                <MagnifyingGlassIcon width={25} />
                            )}
                        </button>
                    ),
                    position: "right",
                }}
                containerClassNames="flex-1"
                className="max-sm:pl-4"
                placeholder={T(inputPlaceholder).result}
                name="Fund Search"
                value={searchText}
                buttonProps={{
                    text: (
                        <Translate>
                            {isActive
                                ? `Clear ${isFiltering ? "all" : "search"}`
                                : "Search funds"}
                        </Translate>
                    ),
                    color: "primary",
                    className:
                        "font-normal text-[0px] sm:text-sm md:text-base max-sm:p-3 sm:px-4 gap-0 max-sm:rounded-l-sm sm:min-w-[126px]",
                    onClick: createClearSearchHandler("all"),
                    icon: isActive ? (
                        <XMarkIcon className="mx-0 h-5 w-5 sm:hidden" />
                    ) : (
                        <MagnifyingGlassIcon className="mx-0 h-5 w-5 sm:hidden" />
                    ),
                }}
                onFocus={handleInputFocus}
                onChange={(e) => setSearchText(e.target.value)}
            />
            <DownloadDataButton />
        </div>
    );
};

export default SearchBar;
